// Debug Logger — centralized in-memory logging for developer debugging
// No external services, no sensitive data, pure client-side

declare global {
  interface Window {
    __REFIFY_DEBUG__?: boolean;
  }
}

export type LogLevel = 'info' | 'warn' | 'error';
export type LogScope = 'preview' | 'auth' | 'api' | 'ui' | 'system' | 'nav' | 'data' | 'quickfill';

export interface DebugLogEntry {
  id: string;
  timestamp: number;
  sessionId: string;
  level: LogLevel;
  scope: LogScope;
  event: string;
  data: unknown;
  context?: string;
  count: number;
}

const MAX_ENTRIES = 200;
const DEDUP_WINDOW_MS = 500;
const THROTTLE_MS = 400;
const throttleMap = new Map<string, number>();

const sessionId = typeof crypto !== 'undefined' && crypto.randomUUID
  ? crypto.randomUUID()
  : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

let logs: DebugLogEntry[] = [];
const lastSuccessMap = new Map<string, DebugLogEntry>();

function isEnabled(): boolean {
  try {
    return import.meta.env.DEV || window.__REFIFY_DEBUG__ === true;
  } catch {
    return false;
  }
}

// Allow enabling debug mode at runtime (e.g. for owners in production)
export function enableDebugMode(): void {
  window.__REFIFY_DEBUG__ = true;
}

function serializeForCompare(data: unknown): string {
  try {
    return JSON.stringify(data);
  } catch {
    return String(data);
  }
}

export function debugLog(
  level: LogLevel,
  scope: LogScope,
  event: string,
  data: unknown,
  context?: string,
): void {
  if (!isEnabled()) return;

  // Enrich error data
  let enrichedData = data;
  if (level === 'error' && data instanceof Error) {
    enrichedData = {
      message: data.message,
      stack: data.stack,
      name: data.name,
    };
  }

  const now = Date.now();

  // Deduplication check (identical data within window)
  const last = logs[0];
  if (
    last &&
    last.level === level &&
    last.scope === scope &&
    last.event === event &&
    now - last.timestamp < DEDUP_WINDOW_MS &&
    serializeForCompare(enrichedData) === serializeForCompare(last.data)
  ) {
    last.count += 1;
    return;
  }

  // Throttle check (same scope:event, different data, within throttle window)
  const throttleKey = `${scope}:${event}`;
  const lastThrottleTime = throttleMap.get(throttleKey);
  if (lastThrottleTime && now - lastThrottleTime < THROTTLE_MS) {
    const existing = logs.find(l => l.scope === scope && l.event === event);
    if (existing) {
      existing.data = enrichedData;
      existing.timestamp = now;
      existing.count += 1;
      throttleMap.set(throttleKey, now);
      return;
    }
  }
  throttleMap.set(throttleKey, now);

  const entry: DebugLogEntry = {
    id: `${now}-${Math.random().toString(36).slice(2, 8)}`,
    timestamp: now,
    sessionId,
    level,
    scope,
    event,
    data: enrichedData,
    context,
    count: 1,
  };

  logs.unshift(entry);
  if (logs.length > MAX_ENTRIES) {
    logs = logs.slice(0, MAX_ENTRIES);
  }

  // Track last success state for info-level events
  if (level === 'info') {
    lastSuccessMap.set(`${scope}:${event}`, entry);
  }
}

export function getDebugLogs(): DebugLogEntry[] {
  return [...logs];
}

export function clearDebugLogs(): void {
  logs = [];
}

export function getLastSuccess(key: string): DebugLogEntry | undefined {
  return lastSuccessMap.get(key);
}


export function getSessionId(): string {
  return sessionId;
}

// Global error capture — call once from main.tsx
export function initGlobalErrorCapture(): void {
  if (!isEnabled()) return;

  window.onerror = (message, source, lineno, colno, error) => {
    debugLog('error', 'system', 'UNCAUGHT_ERROR', {
      message: String(message),
      source,
      line: lineno,
      col: colno,
      stack: error?.stack,
    });
  };

  window.onunhandledrejection = (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    debugLog('error', 'system', 'UNHANDLED_REJECTION', {
      reason: reason instanceof Error ? reason.message : String(reason),
      stack: reason instanceof Error ? reason.stack : undefined,
    });
  };
}
